import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type AutomationRuleStatus = 'draft' | 'dry_run' | 'active' | 'paused' | 'disabled';

export interface AutomationRecipe {
  id: string;
  version: number;
  materialVersion: number;
  titleKey: string;
  descriptionKey: string;
  riskClass: string;
  confirmationPolicy: string;
  managePermissions: string[];
  triggers: Array<{ kind: string; insightType?: string }>;
  allowedCommands: Array<{ name: string; descriptionKey: string; requiredPermissions: string[] }>;
  parameterKeys: string[];
  defaultParameters: Record<string, unknown>;
  defaultLimits: { maxRunsPerDay?: number; cooldownMs?: number; maxConcurrentRuns?: number };
  linkedInsightTypes: string[];
  dryRunSupported: boolean;
}

export interface AutomationRule {
  id: string;
  tenantId: string;
  recipeId: string;
  recipeVersion: number;
  recipeMaterialVersion: number;
  name: string;
  description: string | null;
  status: AutomationRuleStatus;
  pauseReason: string | null;
  ownerUserId: string;
  scope: Record<string, unknown>;
  parameters: Record<string, unknown>;
  notificationUserIds: string[];
  limits: { maxRunsPerDay?: number; cooldownMs?: number; maxConcurrentRuns?: number };
  validFrom: string | null;
  validUntil: string | null;
  version: number;
  lastDryRunAt: string | null;
  lastRunAt: string | null;
  activatedAt: string | null;
  activatedBy: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  needsReapproval: boolean;
}

export interface AutomationTenantSettings {
  killSwitch: boolean;
  paused: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AutomationRuleEvent {
  id: string;
  ruleId: string;
  seq: number;
  eventType: string;
  actorType: string;
  actorId: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AutomationRuleRun {
  id: string;
  ruleId: string;
  recipeId: string;
  mode: 'dry_run' | 'live';
  status: string;
  triggerKind: string;
  error: string | null;
  resultSummary: Record<string, unknown> | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface AutomationRuleRunStep {
  id: string;
  stepIndex: number;
  commandName: string;
  status: string;
  request: Record<string, unknown>;
  response: Record<string, unknown> | null;
  error: string | null;
}

export function useAutomationRecipes(enabled = true) {
  return useQuery({
    queryKey: ['automation', 'recipes'],
    queryFn: async () => {
      const res = await api.get<{ recipes: AutomationRecipe[] }>('/automation-recipes');
      return res.recipes;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAutomationRules(enabled = true) {
  return useQuery({
    queryKey: ['automation', 'rules'],
    queryFn: async () => {
      const res = await api.get<{ rules: AutomationRule[] }>('/automation-rules');
      return res.rules;
    },
    enabled,
  });
}

export function useAutomationSettings(enabled = true) {
  return useQuery({
    queryKey: ['automation', 'settings'],
    queryFn: async () => {
      const res = await api.get<{ settings: AutomationTenantSettings }>('/automation-rules/settings');
      return res.settings;
    },
    enabled,
  });
}

export function useAutomationRuleEvents(ruleId: string | null) {
  return useQuery({
    queryKey: ['automation', 'rules', ruleId, 'events'],
    queryFn: async () => {
      const res = await api.get<{ events: AutomationRuleEvent[] }>(`/automation-rules/${ruleId}/events`);
      return res.events;
    },
    enabled: Boolean(ruleId),
  });
}

export function useAutomationRuleRuns(ruleId: string | null) {
  return useQuery({
    queryKey: ['automation', 'runs', ruleId],
    queryFn: async () => {
      const res = await api.get<{ runs: AutomationRuleRun[] }>(
        `/automation-rule-runs?ruleId=${encodeURIComponent(ruleId ?? '')}&limit=20`,
      );
      return res.runs;
    },
    enabled: Boolean(ruleId),
  });
}

export function useAutomationRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ['automation', 'run', runId],
    queryFn: async () => {
      return api.get<{ run: AutomationRuleRun; steps: AutomationRuleRunStep[] }>(
        `/automation-rule-runs/${runId}`,
      );
    },
    enabled: Boolean(runId),
  });
}

function invalidateAutomation(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['automation'] });
}

export function useCreateAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { recipeId: string; name: string; description?: string; parameters?: Record<string, unknown> }) =>
      api.post<{ rule: AutomationRule }>('/automation-rules', body),
    onSuccess: () => invalidateAutomation(queryClient),
  });
}

export function useTransitionAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      expectedVersion,
    }: {
      id: string;
      action: 'dry-run-mode' | 'activate' | 'pause' | 'resume' | 'disable' | 'reapprove';
      expectedVersion?: number;
    }) =>
      api.post<{ rule: AutomationRule }>(`/automation-rules/${id}/${action}`, {
        ...(expectedVersion != null ? { expectedVersion } : {}),
      }),
    onSuccess: () => invalidateAutomation(queryClient),
  });
}

export function useStartAutomationRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      mode,
      input,
    }: {
      id: string;
      mode: 'dry_run' | 'live';
      input?: Record<string, unknown>;
    }) =>
      api.post<{ runId: string; status: string; deduped: boolean }>(`/automation-rules/${id}/runs`, {
        mode,
        ...(input ? { input } : {}),
      }),
    onSuccess: () => invalidateAutomation(queryClient),
  });
}

export function useUpdateAutomationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { killSwitch?: boolean; paused?: boolean }) =>
      api.patch<{ settings: AutomationTenantSettings }>('/automation-rules/settings', body),
    onSuccess: () => invalidateAutomation(queryClient),
  });
}

export function recipeForInsightType(
  recipes: AutomationRecipe[] | undefined,
  insightType: string,
): AutomationRecipe | undefined {
  return recipes?.find((recipe) => recipe.linkedInsightTypes.includes(insightType));
}
